import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { selectStyles } from '../../components/base/forms/formikSelect';
import { ReactComponent as IconParcel } from '../../icons/parcel.svg';
import { IFormSelectOption } from '../../interfaces/forms/forms';
import { Label } from '../base/forms/label';
import { Column, Columns } from '../base/grid';
import { A, P } from '../base/typography';
import * as StyledShipmentCalculatorForm from './shipmentCalculatorForm.styles';

interface Props {}

const ShipmentCalculatorForm: FC<Props> = () => {
	const { t } = useTranslation('shipmentCalculator');
	const selectOptions: IFormSelectOption[] = [
		{ value: '500', label: t('selectOptions.1') },
		{ value: '660', label: t('selectOptions.2') },
		{ value: '990', label: t('selectOptions.3') },
		{ value: '1350', label: t('selectOptions.4') },
	];
	const [administrativeFee, setAdministrativeFee]: [IFormSelectOption, any] = useState(selectOptions[0]);
	const [sendingFee, setSendingFee]: [[number, number], any] = useState([0, 750]);
	const [valueAddedTax, setValueAddedTax]: [[number, number], any] = useState([0, 24]);
	const [productPrice, setProductPrice]: [string, any] = useState('');
	const [e1CustomsReportCharge, setE1CustomsReportCharge]: [number, any] = useState(0);
	const [tollkritCharge, setTollkritCharge]: [number, any] = useState(0);

	const onValueAddedTaxClick = (values: [number, number]) => {
		setValueAddedTax(values);
	};

	const onSendingFeeClick = (values: [number, number]) => {
		setSendingFee(values);
	};

	const onAdministrativeFeeChange = (values: IFormSelectOption) => {
		setAdministrativeFee(values);
	};

	const onProductPriceChange = (e: any) => {
		setProductPrice(e.target.value);
	};
	const productPriceNumber = (productPrice && Number(productPrice)) || 0;
	const totalPrice = Math.floor(
		productPriceNumber +
			(valueAddedTax[1] / 100) * productPriceNumber +
			Number(administrativeFee.value) +
			sendingFee[1] +
			e1CustomsReportCharge +
			tollkritCharge
	)
		.toString()
		.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1.');

	// If product price goes over 40.000 we have to charge for E1 customs report
	useEffect(() => {
		if (productPriceNumber > 40000) {
			setE1CustomsReportCharge(5700);
			setTollkritCharge(1215);
		} else if (productPriceNumber <= 40000 && e1CustomsReportCharge !== 0) {
			setE1CustomsReportCharge(0);
			setTollkritCharge(0);
		}
	}, [setE1CustomsReportCharge, productPriceNumber, e1CustomsReportCharge, setTollkritCharge]);

	const productPriceString = Math.floor(productPriceNumber)
		.toString()
		.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1.');

	const sendingFeeString = Math.floor((valueAddedTax[1] / 100) * productPriceNumber)
		.toString()
		.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1.');

	const totalFees = Math.floor(
		(valueAddedTax[1] / 100) * productPriceNumber +
			Number(administrativeFee.value) +
			Number(sendingFee[1] + e1CustomsReportCharge + tollkritCharge)
	)
		.toString()
		.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1.');

	const e1CustomsReportChargeString = e1CustomsReportCharge.toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1.');
	const tollkritChargeString = tollkritCharge.toString().replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1.');

	return (
		<StyledShipmentCalculatorForm.Wrapper>
			<Label>{`${t('priceOfShipment')}`}</Label>
			<Columns>
				<Column tablet={1 / 2}>
					<StyledShipmentCalculatorForm.InputStyles
						type="tel"
						min="0"
						value={productPrice}
						onChange={onProductPriceChange}
					/>
				</Column>
			</Columns>
			<Label marginTop="3rem">{t('administrativeFee')}</Label>
			{/* <StyledShipmentCalculatorForm.InfoText>{t('administrativeFeeText')}</StyledShipmentCalculatorForm.InfoText> */}
			<Columns>
				<Column>
					<StyledShipmentCalculatorForm.SelectWrapper>
						<Select
							id="administrativeFee"
							name="administrativeFee"
							options={selectOptions}
							onChange={onAdministrativeFeeChange}
							styles={selectStyles}
							value={administrativeFee}
						/>
					</StyledShipmentCalculatorForm.SelectWrapper>
				</Column>
			</Columns>
			<Label marginTop="3rem" marginBottom="0rem">
				{t('sendingFee')}
			</Label>
			<StyledShipmentCalculatorForm.InfoText>{t('shipmentFeeText')}</StyledShipmentCalculatorForm.InfoText>
			<Columns>
				<Column tablet={1 / 2}>
					<StyledShipmentCalculatorForm.RadioWrapper
						checked={sendingFee[0] === 0}
						onClick={onSendingFeeClick.bind(null, [0, 750])}>
						<StyledShipmentCalculatorForm.RadioTileLabelWithIcon>
							<IconParcel />
							<P>{t('sendingFeeEurope')}</P>
							<StyledShipmentCalculatorForm.SendingFeePrice>
								750 kr.
							</StyledShipmentCalculatorForm.SendingFeePrice>
						</StyledShipmentCalculatorForm.RadioTileLabelWithIcon>
					</StyledShipmentCalculatorForm.RadioWrapper>
				</Column>
				<Column tablet={1 / 2}>
					<StyledShipmentCalculatorForm.RadioWrapper
						checked={sendingFee[0] === 1}
						onClick={onSendingFeeClick.bind(null, [1, 950])}>
						<StyledShipmentCalculatorForm.RadioTileLabelWithIcon>
							<IconParcel />
							<P>{t('sendingFeeOutsideEurope')}</P>
							<StyledShipmentCalculatorForm.SendingFeePrice>
								950 kr.
							</StyledShipmentCalculatorForm.SendingFeePrice>
						</StyledShipmentCalculatorForm.RadioTileLabelWithIcon>
					</StyledShipmentCalculatorForm.RadioWrapper>
				</Column>
			</Columns>
			<Label marginTop="3rem" marginBottom="0">
				{t('valueAddedTax')}
			</Label>
			<StyledShipmentCalculatorForm.InfoText>
				{t('vskText')}
				&nbsp;
				<A href="https://www.tollur.is/einstaklingar/tollamal/reiknivel/" target="_blank">
					{t('link')}
				</A>
			</StyledShipmentCalculatorForm.InfoText>
			<Columns>
				<Column tablet={1 / 3}>
					<StyledShipmentCalculatorForm.RadioWrapper
						checked={valueAddedTax[0] === 0}
						onClick={onValueAddedTaxClick.bind(null, [0, 24])}>
						<StyledShipmentCalculatorForm.RadioTileLabel>
							<P>{`${t('valueAddedTaxShort')} 24%`}</P>
						</StyledShipmentCalculatorForm.RadioTileLabel>
					</StyledShipmentCalculatorForm.RadioWrapper>
				</Column>
				<Column tablet={1 / 3}>
					<StyledShipmentCalculatorForm.RadioWrapper
						checked={valueAddedTax[0] === 1}
						onClick={onValueAddedTaxClick.bind(null, [1, 11])}>
						<StyledShipmentCalculatorForm.RadioTileLabel>
							<P>{`${t('valueAddedTaxShort')} 11%`}</P>
						</StyledShipmentCalculatorForm.RadioTileLabel>
					</StyledShipmentCalculatorForm.RadioWrapper>
				</Column>
				<Column tablet={1 / 3}>
					<StyledShipmentCalculatorForm.RadioWrapper
						checked={valueAddedTax[0] === 2}
						onClick={onValueAddedTaxClick.bind(null, [2, 0])}>
						<StyledShipmentCalculatorForm.RadioTileLabel>
							<P>{`${t('valueAddedTaxShort')} 0%`}</P>
						</StyledShipmentCalculatorForm.RadioTileLabel>
					</StyledShipmentCalculatorForm.RadioWrapper>
				</Column>
			</Columns>
			<Label marginTop="3rem">{t('breakdown')}</Label>
			<StyledShipmentCalculatorForm.ExpenseWrapper>
				<StyledShipmentCalculatorForm.BorderColumns>
					<StyledShipmentCalculatorForm.TextColumn mobile={2 / 3}>
						<StyledShipmentCalculatorForm.Expense>
							{t('priceInIcelandicKronurs')}
						</StyledShipmentCalculatorForm.Expense>
					</StyledShipmentCalculatorForm.TextColumn>
					<StyledShipmentCalculatorForm.TextColumn mobile={1 / 3}>
						<StyledShipmentCalculatorForm.Expense right>
							<strong>{`${productPriceString} kr.`}</strong>
						</StyledShipmentCalculatorForm.Expense>
					</StyledShipmentCalculatorForm.TextColumn>
				</StyledShipmentCalculatorForm.BorderColumns>
				<StyledShipmentCalculatorForm.BorderColumns>
					<StyledShipmentCalculatorForm.TextColumn mobile={2 / 3}>
						<StyledShipmentCalculatorForm.Expense>{t('importFee')}</StyledShipmentCalculatorForm.Expense>
					</StyledShipmentCalculatorForm.TextColumn>
					<StyledShipmentCalculatorForm.TextColumn mobile={1 / 3}>
						<StyledShipmentCalculatorForm.Expense right>
							<strong>{`${sendingFeeString} kr.`}</strong>
						</StyledShipmentCalculatorForm.Expense>
					</StyledShipmentCalculatorForm.TextColumn>
				</StyledShipmentCalculatorForm.BorderColumns>
				<StyledShipmentCalculatorForm.BorderColumns>
					<StyledShipmentCalculatorForm.TextColumn mobile={2 / 3}>
						<StyledShipmentCalculatorForm.Expense>
							{t('administrativeFee')}
						</StyledShipmentCalculatorForm.Expense>
					</StyledShipmentCalculatorForm.TextColumn>
					<StyledShipmentCalculatorForm.TextColumn mobile={1 / 3}>
						<StyledShipmentCalculatorForm.Expense right>
							<strong>{`${administrativeFee.value} kr.`}</strong>
						</StyledShipmentCalculatorForm.Expense>
					</StyledShipmentCalculatorForm.TextColumn>
				</StyledShipmentCalculatorForm.BorderColumns>
				<StyledShipmentCalculatorForm.BorderColumns>
					<StyledShipmentCalculatorForm.TextColumn mobile={2 / 3}>
						<StyledShipmentCalculatorForm.Expense>{t('sendingFee')}</StyledShipmentCalculatorForm.Expense>
					</StyledShipmentCalculatorForm.TextColumn>
					<StyledShipmentCalculatorForm.TextColumn mobile={1 / 3}>
						<StyledShipmentCalculatorForm.Expense right>
							<strong>{`${sendingFee[1]} kr.`}</strong>
						</StyledShipmentCalculatorForm.Expense>
					</StyledShipmentCalculatorForm.TextColumn>
				</StyledShipmentCalculatorForm.BorderColumns>
				<StyledShipmentCalculatorForm.BorderColumns>
					<StyledShipmentCalculatorForm.TextColumn mobile={2 / 3}>
						<StyledShipmentCalculatorForm.Expense>{t('e1CustomsFee')}</StyledShipmentCalculatorForm.Expense>
					</StyledShipmentCalculatorForm.TextColumn>
					<StyledShipmentCalculatorForm.TextColumn mobile={1 / 3}>
						<StyledShipmentCalculatorForm.Expense right>
							<strong>{`${e1CustomsReportChargeString} kr.`}</strong>
						</StyledShipmentCalculatorForm.Expense>
					</StyledShipmentCalculatorForm.TextColumn>
				</StyledShipmentCalculatorForm.BorderColumns>
				<StyledShipmentCalculatorForm.BorderColumns>
					<StyledShipmentCalculatorForm.TextColumn mobile={2 / 3}>
						<StyledShipmentCalculatorForm.Expense>{t('tollkrit')}</StyledShipmentCalculatorForm.Expense>
					</StyledShipmentCalculatorForm.TextColumn>
					<StyledShipmentCalculatorForm.TextColumn mobile={1 / 3}>
						<StyledShipmentCalculatorForm.Expense right>
							<strong>{`${tollkritChargeString} kr.`}</strong>
						</StyledShipmentCalculatorForm.Expense>
					</StyledShipmentCalculatorForm.TextColumn>
				</StyledShipmentCalculatorForm.BorderColumns>
				<StyledShipmentCalculatorForm.BorderColumns>
					<StyledShipmentCalculatorForm.TextColumn mobile={2 / 3}>
						<StyledShipmentCalculatorForm.Expense strong primary>
							{t('totalFee')}
						</StyledShipmentCalculatorForm.Expense>
					</StyledShipmentCalculatorForm.TextColumn>
					<StyledShipmentCalculatorForm.TextColumn mobile={1 / 3}>
						<StyledShipmentCalculatorForm.Expense right strong primary>
							<strong>{`${totalFees} kr.*`}</strong>
						</StyledShipmentCalculatorForm.Expense>
					</StyledShipmentCalculatorForm.TextColumn>
				</StyledShipmentCalculatorForm.BorderColumns>
				<StyledShipmentCalculatorForm.BorderColumns big>
					<StyledShipmentCalculatorForm.TextColumn mobile={1 / 2}>
						<StyledShipmentCalculatorForm.Expense secondary>
							{t('total')}
						</StyledShipmentCalculatorForm.Expense>
					</StyledShipmentCalculatorForm.TextColumn>
					<StyledShipmentCalculatorForm.TextColumn mobile={1 / 2}>
						<StyledShipmentCalculatorForm.Expense right secondary>
							<strong>{`${totalPrice} kr.`}</strong>
						</StyledShipmentCalculatorForm.Expense>
					</StyledShipmentCalculatorForm.TextColumn>
				</StyledShipmentCalculatorForm.BorderColumns>
			</StyledShipmentCalculatorForm.ExpenseWrapper>
			<StyledShipmentCalculatorForm.InfoText>
				<StyledShipmentCalculatorForm.Ref>*&nbsp;</StyledShipmentCalculatorForm.Ref>
				{t('infoText3')}
			</StyledShipmentCalculatorForm.InfoText>
			<StyledShipmentCalculatorForm.InfoText>
				<StyledShipmentCalculatorForm.Ref>*&nbsp;</StyledShipmentCalculatorForm.Ref>
				{t('infoText')}
			</StyledShipmentCalculatorForm.InfoText>
			<StyledShipmentCalculatorForm.InfoText>
				<StyledShipmentCalculatorForm.Ref>*&nbsp;</StyledShipmentCalculatorForm.Ref>
				{t('infoText2')}
				&nbsp;
				<A href="https://www.tollur.is/einstaklingar/tollamal/reiknivel/" target="_blank">
					{t('link')}
				</A>
			</StyledShipmentCalculatorForm.InfoText>
		</StyledShipmentCalculatorForm.Wrapper>
	);
};

export default ShipmentCalculatorForm;
